import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AuthGuardSkeleton } from "@/components/skeletons/PageSkeletons";

const CREATOR_ONLY_ROUTES = ["/inbox", "/analytics"];
const AGENCY_ONLY_ROUTES = ["/agency/inbox", "/agency/roster", "/agency/settings", "/agency/onboarding"];

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, user, profile, loading, refreshProfile } = useAuth();
  const [mfaChecked, setMfaChecked] = useState(false);
  const [needsMfa, setNeedsMfa] = useState(false);
  const [autoClaimingInvite, setAutoClaimingInvite] = useState(false);
  const location = useLocation();
  const autoOnboardingRef = useRef(false);
  const autoClaimRef = useRef(false);

  useEffect(() => {
    if (!session) {
      setMfaChecked(true);
      return;
    }

    const checkMfa = async () => {
      try {
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalData && aalData.nextLevel === "aal2" && aalData.currentLevel === "aal1") {
          setNeedsMfa(true);
        }
      } catch {
        // If MFA check fails, proceed without MFA
      }
      setMfaChecked(true);
    };

    checkMfa();
  }, [session]);

  // Auto-claim team invite for accounts created via agency team invite link
  useEffect(() => {
    if (!profile || !user || autoClaimRef.current) return;
    // Only process if user has a pending team_invite_token and is NOT yet in an agency
    const teamInviteToken = user.user_metadata?.team_invite_token;
    if (!teamInviteToken || profile.agency_id) return;
    // Only claim if account_type is team_member (set during signup)
    if (user.user_metadata?.account_type !== "team_member") return;

    autoClaimRef.current = true;
    setAutoClaimingInvite(true);

    const claimInvite = async () => {
      try {
        console.log("[AUTO-CLAIM] Attempting to auto-claim team invite", teamInviteToken);
        const { data, error } = await supabase.functions.invoke("claim-team-invite", {
          body: { invite_id: teamInviteToken, action: "accept" },
        });

        if (error || data?.error) {
          console.error("[AUTO-CLAIM] Failed:", data?.error || error);
          // Invite is invalid/expired — delete the orphaned account
          console.log("[AUTO-CLAIM] Deleting orphaned team invite account");
          try {
            await supabase.functions.invoke("delete-account");
          } catch (delErr) {
            console.error("[AUTO-CLAIM] Account deletion failed:", delErr);
          }
          // Sign out locally and redirect to a clear message
          try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
          window.location.href = `/login?team_invite_expired=true`;
          return;
        }

        console.log("[AUTO-CLAIM] Successfully joined agency", data?.agency_id);
        // Clear the token from metadata
        await supabase.auth.updateUser({
          data: { team_invite_token: null },
        });

        await refreshProfile();
      } catch (err) {
        console.error("[AUTO-CLAIM] Error:", err);
      } finally {
        setAutoClaimingInvite(false);
      }
    };

    claimInvite();
  }, [profile, user, refreshProfile]);

  // (onboarding is handled by the redirect below — creators go through the 4-step flow)

  const isWaiting = loading || !mfaChecked || autoClaimingInvite;

  if (isWaiting) {
    return <AuthGuardSkeleton />;
  }

  if (!session) return <Navigate to="/login" replace />;

  if (needsMfa) return <Navigate to="/mfa-verify" replace />;

  // Check if email is confirmed (safety net)
  if (user && !user.email_confirmed_at) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email || "")}`} replace />;
  }

  // Redirect to onboarding if not completed
  const isOnboardingRoute = location.pathname.startsWith("/onboarding") || location.pathname.startsWith("/agency/onboarding");
  if (profile && !profile.onboarding_done && !isOnboardingRoute) {
    const accountType = user?.user_metadata?.account_type;
    const isAgencyUser = accountType === "agency" || accountType === "team_member" || !!profile.agency_id;
    return <Navigate to={isAgencyUser ? "/agency/onboarding" : "/onboarding"} replace />;
  }

  // Determine if user is agency (by profile or metadata for pre-onboarding)
  const isAgency = !!profile?.agency_id || user?.user_metadata?.account_type === "agency" || user?.user_metadata?.account_type === "team_member";

  // Prevent agency users from accessing creator-only routes
  if (isAgency && CREATOR_ONLY_ROUTES.some(r => location.pathname.startsWith(r))) {
    return <Navigate to="/agency/inbox" replace />;
  }

  // Prevent creator users from accessing agency routes (skip onboarding routes, handled above)
  if (!isAgency && profile && !isOnboardingRoute && AGENCY_ONLY_ROUTES.some(r => location.pathname.startsWith(r))) {
    return <Navigate to="/inbox" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;